
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService } from './services/login.service';
import { User } from './models/user';
import { LanguageService } from './services/translation.service';
import { first, Subject, Subscription, takeUntil } from 'rxjs';
import { CommonService } from './services/common.service';
import { SocketService } from './services/socket.service';
import { MenuComponent } from './menu/menu.component';
import { TranslateModule } from '@ngx-translate/core';

import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: true,
    imports: [MenuComponent, TranslateModule, FormsModule, RouterModule]
})
export class AppComponent implements OnInit, OnDestroy {
  showReconnectingAlert = false;
  private reconnectSubscription: Subscription;
  title = 'protean';
  currentUser: User = new User();
  private unsubscribe$ = new Subject<void>();

  constructor(
    private loginService: LoginService,
    private router: Router,
    private languageService: LanguageService,
    private commonService: CommonService,
    private socketService: SocketService
  ) {
    this.loginService.currentUser.subscribe(x => this.currentUser = x);
  }

  ngOnInit(): void {
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
    this.languageService.initializeLanguage();
    //checking the global socket status
    this.reconnectSubscription = this.socketService.reconnecting$.subscribe(
      isReconnecting => {
        console.log('in app component value: ',isReconnecting);  
        this.showReconnectingAlert = isReconnecting;
      }
    );
    // Check for SAML return
    this.checkSamlReturn();
  }

checkSamlReturn() {
  const urlParams = new URLSearchParams(window.location.search);
  const samlSuccess = urlParams.get('saml_success');
  const samlError = urlParams.get('saml_error');
  const logoutSuccess = urlParams.get('logout');
  const token = urlParams.get('token');

  console.log('Checking SAML return:', { samlSuccess, samlError, logoutSuccess, token: token ? 'exists' : 'none' });

  if (logoutSuccess === 'success') {
    window.history.replaceState({}, document.title, window.location.pathname);
    
    if (samlError === 'user_not_found') {
      console.log('User not found - Keycloak session cleared');
      this.router.navigate(['/login'], { 
        queryParams: { 
          saml_error: 'user_not_found',
          error_message: 'User not found in database. Your Keycloak session has been cleared. Please contact your administrator.'
        } 
      });
    }
    return;
  }

  if (samlSuccess === 'true' && token) {
    window.history.replaceState({}, document.title, window.location.pathname);
    this.processSamlSuccess(token);
  } else if (samlError) {
    window.history.replaceState({}, document.title, window.location.pathname);
    
    let errorMessage;
    
    switch (samlError) {
      case 'user_not_found':
        errorMessage = 'User not found in database. Please contact your administrator.';
        break;
      case 'no_email_in_profile':
        errorMessage = 'Email not found in SAML profile. Please contact your administrator.';
        break;
      case 'auth_failed':
        errorMessage = 'SAML authentication failed. Please try again or use email/password login.';
        break;
      case 'login_error':
        errorMessage = 'Login error occurred. Please try again.';
        break;
      case 'server_error':
        errorMessage = 'Server error occurred. Please try again later.';
        break;
      default:
        errorMessage = `Authentication error: ${samlError}`;
    }
    
    this.router.navigate(['/login'], { 
      queryParams: { 
        saml_error: samlError,
        error_message: errorMessage
      } 
    });
  }
}
  processSamlSuccess(token: string) {
    this.loginService.initializeSamlSession(token).pipe(first()).subscribe({
      next: (initResponse: any) => {
        if (initResponse.success) {
          this.loginService.getUserObject().pipe(first()).subscribe({
            next: (userObjResponse: any) => {
              let user = new User();
              user.email = initResponse.email;
              user.config = userObjResponse.config;
              user.userObj = userObjResponse.user;

              this.loginService.setcurrentUserSubject(user);
              localStorage.setItem('currentUser', JSON.stringify(user));
              localStorage.setItem('loginMethod', 'saml');
              sessionStorage.setItem('currentUser', JSON.stringify(user));
              sessionStorage.setItem('language', 'en');
              this.commonService.setActiveTab('/devices');
              this.router.navigate(['/devices']);
            },
            error: (error) => {
              this.router.navigate(['/login'], { 
                queryParams: { error: 'user_object_error' } 
              });
            }
          });
        } else {
          this.router.navigate(['/login'], { 
            queryParams: { error: 'session_init_failed' } 
          });
        }
      },
      error: (error) => {
        this.router.navigate(['/login'], { 
          queryParams: { error: 'session_error' } 
        });
      }
    });
  }

  handleStorageEvent(event: StorageEvent) {
    if (event.key === 'logoutEvent') {
      sessionStorage.removeItem('currentUser');
      this.router.navigate(['/login']);
      location.reload();
    }
  }

  ngOnDestroy() {
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));
    if (this.reconnectSubscription) {
      this.reconnectSubscription.unsubscribe();
    }
  }
}